/**
 * IndexedDB wrapper for offline-first caching of buitendienst data.
 * Stores: dossiers, opdrachten, checklists, photos (blobs), sync queue.
 */

const DB_NAME = 'cbridge-offline';
const DB_VERSION = 1;

export interface SyncQueueItem {
  id: string;
  type: 'checklist_afvinken' | 'opdracht_invullen' | 'foto_upload' | 'gps_checkin' | 'handtekening' | 'checklist_foto_upload';
  url: string;
  method: string;
  body?: any;
  /** For file uploads: stored as ArrayBuffer + metadata */
  fileData?: { buffer: ArrayBuffer; name: string; type: string };
  metadata?: Record<string, string>;
  createdAt: number;
  retries: number;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;

      // Dossier cache (key = dossier id)
      if (!db.objectStoreNames.contains('dossiers')) {
        db.createObjectStore('dossiers', { keyPath: 'id' });
      }

      // Dossier list cache (key = filter hash)
      if (!db.objectStoreNames.contains('dossierLists')) {
        db.createObjectStore('dossierLists', { keyPath: 'key' });
      }

      // Checklist items cache
      if (!db.objectStoreNames.contains('checklists')) {
        db.createObjectStore('checklists', { keyPath: 'trajectstapId' });
      }

      // Opdrachten cache
      if (!db.objectStoreNames.contains('opdrachten')) {
        db.createObjectStore('opdrachten', { keyPath: 'dossierId' });
      }

      // Sync queue for offline mutations
      if (!db.objectStoreNames.contains('syncQueue')) {
        const store = db.createObjectStore('syncQueue', { keyPath: 'id' });
        store.createIndex('createdAt', 'createdAt');
      }

      // Offline photos (blob storage)
      if (!db.objectStoreNames.contains('offlinePhotos')) {
        db.createObjectStore('offlinePhotos', { keyPath: 'id' });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ─── Generic CRUD helpers ──────────────────────────────────────

async function getFromStore<T>(storeName: string, key: string): Promise<T | undefined> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

async function putToStore<T>(storeName: string, value: T): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    store.put(value);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function deleteFromStore(storeName: string, key: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    store.delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function getAllFromStore<T>(storeName: string): Promise<T[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result as T[]);
    req.onerror = () => reject(req.error);
  });
}

async function clearStore(storeName: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    store.clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ─── Dossier cache ─────────────────────────────────────────────

export async function cacheDossier(dossier: any): Promise<void> {
  await putToStore('dossiers', dossier);
}

export async function getCachedDossier(id: string): Promise<any | undefined> {
  return getFromStore('dossiers', id);
}

export async function cacheDossierList(filterKey: string, dossiers: any[]): Promise<void> {
  await putToStore('dossierLists', { key: filterKey, dossiers, cachedAt: Date.now() });
}

export async function getCachedDossierList(filterKey: string): Promise<any[] | undefined> {
  const result = await getFromStore<{ key: string; dossiers: any[]; cachedAt: number }>('dossierLists', filterKey);
  return result?.dossiers;
}

// ─── Checklist cache ───────────────────────────────────────────

export async function cacheChecklist(trajectstapId: string, items: any[]): Promise<void> {
  await putToStore('checklists', { trajectstapId, items, cachedAt: Date.now() });
}

export async function getCachedChecklist(trajectstapId: string): Promise<any[] | undefined> {
  const result = await getFromStore<{ trajectstapId: string; items: any[] }>('checklists', trajectstapId);
  return result?.items;
}

// ─── Opdrachten cache ──────────────────────────────────────────

export async function cacheOpdrachten(dossierId: string, opdrachten: any[]): Promise<void> {
  await putToStore('opdrachten', { dossierId, opdrachten, cachedAt: Date.now() });
}

export async function getCachedOpdrachten(dossierId: string): Promise<any[] | undefined> {
  const result = await getFromStore<{ dossierId: string; opdrachten: any[] }>('opdrachten', dossierId);
  return result?.opdrachten;
}

// ─── Sync queue ────────────────────────────────────────────────

export async function addToSyncQueue(item: SyncQueueItem): Promise<void> {
  await putToStore('syncQueue', item);
}

export async function getSyncQueue(): Promise<SyncQueueItem[]> {
  const items = await getAllFromStore<SyncQueueItem>('syncQueue');
  return items.sort((a, b) => a.createdAt - b.createdAt);
}

export async function removeFromSyncQueue(id: string): Promise<void> {
  await deleteFromStore('syncQueue', id);
}

export async function updateSyncQueueItem(item: SyncQueueItem): Promise<void> {
  await putToStore('syncQueue', item);
}

export async function getSyncQueueCount(): Promise<number> {
  const items = await getAllFromStore<SyncQueueItem>('syncQueue');
  return items.length;
}

// ─── Offline photos ────────────────────────────────────────────

export async function storeOfflinePhoto(id: string, file: File, metadata?: Record<string, any>): Promise<void> {
  const buffer = await file.arrayBuffer();
  await putToStore('offlinePhotos', {
    id,
    buffer,
    name: file.name,
    type: file.type,
    size: file.size,
    metadata,
    createdAt: Date.now(),
  });
}

export async function getOfflinePhoto(id: string): Promise<{ buffer: ArrayBuffer; name: string; type: string } | undefined> {
  return getFromStore('offlinePhotos', id);
}

export async function removeOfflinePhoto(id: string): Promise<void> {
  await deleteFromStore('offlinePhotos', id);
}

export async function getOfflinePhotoCount(): Promise<number> {
  const items = await getAllFromStore('offlinePhotos');
  return items.length;
}

// ─── Utilities ─────────────────────────────────────────────────

export async function clearAllCaches(): Promise<void> {
  await Promise.all([
    clearStore('dossiers'),
    clearStore('dossierLists'),
    clearStore('checklists'),
    clearStore('opdrachten'),
    clearStore('offlinePhotos'),
  ]);
}

/**
 * Cleanup stale cached data older than maxAge (default 7 days).
 * Keeps syncQueue and offlinePhotos intact (those are pending actions).
 */
export async function cleanupStaleCache(maxAgeMs: number = 7 * 24 * 60 * 60 * 1000): Promise<number> {
  const cutoff = Date.now() - maxAgeMs;
  let removed = 0;

  for (const store of ['dossiers', 'dossierLists', 'checklists', 'opdrachten'] as const) {
    try {
      const items = await getAllFromStore(store);
      for (const item of items) {
        const r = item as any;
        const cachedAt = r._cachedAt || r.updatedAt || 0;
        const ts = typeof cachedAt === 'string' ? new Date(cachedAt).getTime() : cachedAt;
        if (ts && ts < cutoff) {
          const key = r.id || r.key || r.trajectstapId || r.dossierId;
          if (key) { await deleteFromStore(store, key); removed++; }
        }
      }
    } catch { /* store may not exist yet */ }
  }

  return removed;
}

export function filterKeyFromParams(filters?: Record<string, string>): string {
  if (!filters || Object.keys(filters).length === 0) return '__all__';
  return Object.entries(filters).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => `${k}=${v}`).join('&');
}
